import React, { useState, FC, HTMLAttributes } from 'react'
import { Link } from 'react-scroll'
import { Box, Flex, Skeleton, Heading, SimpleGrid, List, ListItem, Icon } from '@chakra-ui/core'
import { Card } from '@engyalo/storefront-components'
import { Link as LinkChakra, Button } from '@chakra-ui/core'
import { useParams } from 'react-router'
import { Link as ReactLink } from 'react-router-dom'
import { BoxLinkButton } from '../../lib/types'
import { skipIfInPreviewMode } from '../../utils/mode'
import { categorySelected } from '@engyalo/storefront-events'
import { useRecoilValue, useRecoilState } from 'recoil'
import { configState, orderConfigState } from '../../recoil/atoms'
import { IConfig } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'

import { IProductsPricedPerCollectionCollection } from '@engyalo/storefront-types/lib/graphql/product'
import { Collection } from 'mongoose'
import Error from '../../containers/Error'
import { ErrorTypes } from '../../lib/constants'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  loading: boolean
  products: (IProductsPricedPerCollectionCollection | null)[]
  showVerticalMenu: boolean
}

export const CategoriesMenu: FC<IProps> = ({ loading, products, showVerticalMenu }: IProps) => {
  const [activeSection, setActiveSection] = useState('')
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const { customer, order } = useParams()

  const trackSelectedCategory = (category: string) => (): void => {
    skipIfInPreviewMode(() => {
      categorySelected({
        category,
        session: {
          userID: orderConfig.userID || '',
          customer: config.customer,
          sessionID: orderConfig.id || '',
        },
      })
    })
  }

  const handleSetInactive = (section: string): void => {
    if (activeSection === section) setActiveSection('')
  }

  const handleSetActive = (section: string): void => {
    setActiveSection(section)
    const activeSectionRef = document.getElementById(`${section}-link`)
    if (activeSectionRef) {
      activeSectionRef.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'center',
      })
    }
  }

  const getHorizontalMenu = (): JSX.Element => {
    return (
      <Flex width="100%" overflowX="auto" aria-hidden id="navbar" flex="0 0 auto" zIndex={2} maxW="42.5rem" data-qa-node="categoriesmenu-navbar">
        {loading ? (
          [1, 2, 3, 4].map((number) => <Skeleton key={number} m=".8rem" height="1.6rem" width="10.8rem" />)
        ) : products.length === 0 ? (
          <Box textAlign="center" transform={'translateY(165%)'}>
            <Error type={ErrorTypes.TTL_ERROR} />
          </Box>
        ) : (
          products.map((collection) => {
            const slug = collection?.name || ''
            if (collection) {
              return (
                <Link
                  activeClass="active"
                  onSetActive={handleSetActive}
                  to={slug}
                  spy={true}
                  smooth={true}
                  duration={250}
                  isDynamic={true}
                  containerId="containerElement"
                  onSetInactive={(): void => handleSetInactive(slug)}
                  id={`${slug}-link`}
                  key={`${slug}-link`}
                  onClick={trackSelectedCategory(slug)}
                  data-qa-node="categoriesmenu-category"
                >
                  <Box
                    paddingY="1.6rem"
                    paddingX="4rem"
                    fontSize="categoryTab"
                    borderBottom="0.2rem solid"
                    borderColor={activeSection === slug ? 'primary.200' : 'transparent'}
                    cursor="pointer"
                    whiteSpace="nowrap"
                  >
                    {collection.name}
                  </Box>
                </Link>
              )
            }

            return null
          })
        )}
      </Flex>
    )
  }

  const getVerticalMenu = (): JSX.Element => {
    return (
      <SimpleGrid columns={1} mt="10px" spacing={0} data-qa-node="categoriesmenu-navbar">
        {loading ? (
          [1, 2, 3, 4].map((number) => <Skeleton key={number} m="5px auto" height="4rem" width="90%" />)
        ) : products.length === 0 ? (
          <Box textAlign="center" transform={'translateY(165%)'}>
            <Error type={ErrorTypes.TTL_ERROR} />
          </Box>
        ) : (
          products.map((collection) => {
            const slug = collection?.name || ''
            if (collection) {
              return (
                <>
                  <Box textAlign="center" data-qa-node="categoriesmenu-category">
                    <BoxLinkButton color="teal.500" to={`/${customer}/${order}/category/${collection.id}`}>
                      <Box
                        as="button"
                        height="50px"
                        width="100%"
                        lineHeight="1.2"
                        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
                        border="1px"
                        px="0px"
                        borderRadius="2px"
                        fontSize="14px"
                        fontWeight="semibold"
                        bg="#fafafa"
                        borderColor="#f7f7f7"
                        color="#000000"
                        className="depor-tel"
                      >
                        <Box ml="15px" mt="5px" float="left">
                          {collection.name}
                        </Box>
                        <Icon color="primary.500" float="right" mr="15px" name="chevron-right" size="24px" />
                      </Box>
                    </BoxLinkButton>
                  </Box>
                </>
              )
            }

            return null
          })
        )}
      </SimpleGrid>
    )
  }

  return showVerticalMenu ? getVerticalMenu() : getHorizontalMenu()
}
